import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, IconButton, InputBase } from "@mui/material";
import {
  BadgeCheck,
  CalendarClock,
  Mail,
  Menu,
  Save,
  Search,
  Send,
  ShieldCheck,
  ShieldOff,
  UserRound,
  X,
} from "lucide-react";
import axios from "axios";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorFooter from "../doctor_dashboard/DoctorFooter";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import SimpleSnackbar from "../Snackbar";
import AvailabilityCalendarEditor, {
  buildOneWeekCalendar,
  getAvailabilityWeekProgress,
  isAvailabilityWeekComplete,
  sanitizeCalendarForSave,
} from "../doctor_dashboard/AvailabilityCalendarEditor";
import { isAdminEmail } from "../../utils/adminAccess";

const AdminDoctorsPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [calendar, setCalendar] = useState(() => buildOneWeekCalendar());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState({
    subject: "",
    message: "",
  });
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const doctor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("doctor") || "{}");
    } catch {
      return {};
    }
  }, []);

  const token = localStorage.getItem("token") || "";
  const isAdmin = isAdminEmail(doctor.email);

  const selectedDoctor = useMemo(() => {
    return doctors.find((item) => item._id === selectedDoctorId) || doctors[0];
  }, [doctors, selectedDoctorId]);

  const filteredDoctors = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return doctors;

    return doctors.filter((item) => {
      return (
        item.name?.toLowerCase()?.includes(value) ||
        item.email?.toLowerCase()?.includes(value) ||
        item.phoneNumber?.toLowerCase()?.includes(value)
      );
    });
  }, [doctors, search]);

  const stats = useMemo(() => {
    return doctors.reduce(
      (acc, item) => {
        acc.total += 1;
        if (item.isVerified) acc.verified += 1;
        acc.seen += Number(item.stats?.patientsSeen || 0);
        return acc;
      },
      { total: 0, verified: 0, seen: 0 }
    );
  }, [doctors]);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const showSnack = (message, color = "success") => {
    setSnackMessage(message);
    setSnackColor(color);
  };

  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/doctors`,
        { headers }
      );

      const nextDoctors = res.data?.doctors || [];
      setDoctors(nextDoctors);

      const nextSelected =
        nextDoctors.find((item) => item._id === selectedDoctorId) ||
        nextDoctors[0];

      if (nextSelected) {
        setSelectedDoctorId(nextSelected._id);
        setCalendar(
          buildOneWeekCalendar(nextSelected.availabilityCalendar, {
            markExisting: Boolean(nextSelected.availabilityCalendarUpdatedAt),
          })
        );
      }
    } catch (error) {
      console.error(error);
      showSnack(error.response?.data?.message || "Could not load doctors", "error");
    } finally {
      setLoading(false);
    }
  }, [headers, selectedDoctorId]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    if (!selectedDoctor) return;
    setCalendar(
      buildOneWeekCalendar(selectedDoctor.availabilityCalendar, {
        markExisting: Boolean(selectedDoctor.availabilityCalendarUpdatedAt),
      })
    );
  }, [selectedDoctor]);

  const selectDoctor = (item) => {
    setSelectedDoctorId(item._id);
    setCalendar(
      buildOneWeekCalendar(item.availabilityCalendar, {
        markExisting: Boolean(item.availabilityCalendarUpdatedAt),
      })
    );
  };

  const updateDoctorInList = (doctorId, updates) => {
    setDoctors((prev) =>
      prev.map((item) =>
        item._id === doctorId ? { ...item, ...updates } : item
      )
    );
  };

  const toggleVerification = async (item) => {
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/doctors/${item._id}/verification`,
        { isVerified: !item.isVerified },
        { headers }
      );

      updateDoctorInList(item._id, { isVerified: res.data?.doctor?.isVerified });
      showSnack(res.data?.message || "Doctor updated");
    } catch (error) {
      console.error(error);
      showSnack(
        error.response?.data?.message || "Could not update verification",
        "error"
      );
    }
  };

  const saveAvailability = async () => {
    if (!selectedDoctor?._id) return;

    if (!isAvailabilityWeekComplete(calendar)) {
      showSnack("Please set all 7 days before saving", "error");
      return;
    }

    try {
      setSavingAvailability(true);

      const res = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/doctors/${selectedDoctor._id}/availability`,
        { availabilityCalendar: sanitizeCalendarForSave(calendar) },
        { headers }
      );

      updateDoctorInList(selectedDoctor._id, {
        availabilityCalendar: res.data?.doctor?.availabilityCalendar || calendar,
        availabilityCalendarUpdatedAt:
          res.data?.doctor?.availabilityCalendarUpdatedAt || new Date(),
      });
      showSnack("Availability calendar saved");
    } catch (error) {
      console.error(error);
      showSnack(error.response?.data?.message || "Could not save calendar", "error");
    } finally {
      setSavingAvailability(false);
    }
  };

  const openCustomEmail = (preset = "custom") => {
    if (preset === "availability") {
      setEmailDraft({
        subject: "Please update your Zaviramed availability",
        message:
          "Please log in to your Zaviramed doctor dashboard and update your availability calendar for the next one week. This helps us know when you are ready to receive patients.",
      });
    } else {
      setEmailDraft({
        subject: "",
        message: "",
      });
    }

    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!selectedDoctor?._id) return;

    try {
      setSendingEmail(true);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/doctors/${selectedDoctor._id}/email`,
        emailDraft,
        { headers }
      );

      setEmailOpen(false);
      showSnack("Email sent to doctor");
    } catch (error) {
      console.error(error);
      showSnack(error.response?.data?.message || "Could not send email", "error");
    } finally {
      setSendingEmail(false);
    }
  };

  const sendReminder = async () => {
    if (!selectedDoctor?._id) return;

    try {
      setSendingEmail(true);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/doctors/${selectedDoctor._id}/availability-reminder`,
        {},
        { headers }
      );

      showSnack("Availability reminder sent");
    } catch (error) {
      console.error(error);
      showSnack(error.response?.data?.message || "Could not send reminder", "error");
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <DoctorHeader />
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center">
            <ShieldOff className="mx-auto text-slate-400" size={28} />
            <h1 className="mt-4 text-lg font-semibold text-slate-950">
              Admin only
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              This page is only available to the Zaviramed admin account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const weekProgress = getAvailabilityWeekProgress(calendar);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />
      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        onClose={() => setSnackMessage("")}
      />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">Doctors</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Doctors" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Doctors"
          setActiveTab={() => setDrawerOpen(false)}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                
                <h1 className="text-2xl hidden lg:block font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                  Doctors
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  Verify doctors, manage weekly availability, review patient
                  load, and send updates by email.
                </p>
              </div>

              <Button
                onClick={fetchDoctors}
                disabled={loading}
                sx={{
                  minHeight: 44,
                  borderRadius: "12px",
                  bgcolor: "#0f172a",
                  color: "white",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#1e293b", boxShadow: "none" },
                }}
              >
                {loading ? "Loading..." : "Refresh"}
              </Button>
            </div>

            <section className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatCard label="Doctors" value={stats.total} />
              <StatCard label="Verified" value={stats.verified} />
              <StatCard label="Patients seen" value={stats.seen} />
            </section>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[380px_1fr]">
              <section className="rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-100 p-4">
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Search doctors
                  </label>
                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 px-3">
                    <Search size={16} className="text-slate-400" />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Name, email, phone"
                      className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div className="max-h-[720px] overflow-y-auto p-3">
                  {!filteredDoctors.length && (
                    <p className="px-3 py-10 text-center text-sm text-slate-400">
                      No doctors found.
                    </p>
                  )}

                  <div className="space-y-2">
                    {filteredDoctors.map((item) => (
                      <button
                        key={item._id}
                        type="button"
                        onClick={() => selectDoctor(item)}
                        className={`w-full rounded-2xl border p-4 text-left transition ${
                          selectedDoctor?._id === item._id
                            ? "border-slate-900 bg-slate-50"
                            : "border-slate-200 bg-white hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                            {(item.name || "DR")
                              .split(" ")
                              .filter(Boolean)
                              .map((part) => part[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-950">
                              {item.name || "Unnamed doctor"}
                            </p>
                            <p className="mt-1 truncate text-xs text-slate-500">
                              {item.email || "No email"}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Badge
                                text={item.isVerified ? "Verified" : "Not verified"}
                                active={item.isVerified}
                              />
                              <Badge
                                text={`${Number(
                                  item.stats?.patientsSeen || 0
                                )} seen`}
                              />
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <section className="min-w-0">
                {selectedDoctor ? (
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                              <UserRound size={20} />
                            </div>

                            <div className="min-w-0">
                              <h2 className="truncate text-lg font-semibold text-slate-950">
                                {selectedDoctor.name || "Unnamed doctor"}
                              </h2>
                              <p className="mt-1 truncate text-sm text-slate-500">
                                {selectedDoctor.email || "No email"}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <StatCard
                              label="Assigned"
                              value={selectedDoctor.stats?.assignedPatients || 0}
                              compact
                            />
                            <StatCard
                              label="Patients seen"
                              value={selectedDoctor.stats?.patientsSeen || 0}
                              compact
                            />
                            <StatCard
                              label="Consultations"
                              value={selectedDoctor.stats?.consultations || 0}
                              compact
                            />
                          </div>
                        </div>

                        <div className="grid gap-2 grid-cols-2 lg:w-[360px]">
                          <Button
                            onClick={() => toggleVerification(selectedDoctor)}
                            startIcon={
                              selectedDoctor.isVerified ? (
                                <ShieldOff size={15} />
                              ) : (
                                <ShieldCheck size={15} />
                              )
                            }
                            sx={{
                              minHeight: 42,
                              borderRadius: "12px",
                              bgcolor: selectedDoctor.isVerified
                                ? "#fff"
                                : "#075E54",
                              color: selectedDoctor.isVerified
                                ? "#334155"
                                : "white",
                              border: selectedDoctor.isVerified
                                ? "1px solid #e2e8f0"
                                : "none",
                              textTransform: "none",
                              fontWeight: 600,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: selectedDoctor.isVerified
                                  ? "#f8fafc"
                                  : "#064b43",
                                boxShadow: "none",
                              },
                            }}
                          >
                            {selectedDoctor.isVerified
                              ? "Unverify"
                              : "Verify"}
                          </Button>

                          <Button
                            onClick={() => openCustomEmail("custom")}
                            startIcon={<Mail size={15} />}
                            sx={secondaryButtonSx}
                          >
                            Custom email
                          </Button>

                          <Button
                            onClick={sendReminder}
                            disabled={sendingEmail}
                            startIcon={<Send size={15} />}
                            sx={secondaryButtonSx}
                          >
                            Send reminder
                          </Button>

                          <Button
                            onClick={saveAvailability}
                            disabled={savingAvailability || !weekProgress.isComplete}
                            startIcon={<Save size={15} />}
                            sx={primaryButtonSx}
                          >
                            {savingAvailability
                              ? "Saving..."
                              : weekProgress.isComplete
                              ? "Save calendar"
                              : "Set all days first"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-slate-950">
                            Availability calendar
                          </h3>
                          <p className="mt-1 text-sm text-slate-500">
                            Edit this doctor’s available time for the next one
                            week.
                          </p>
                        </div>

                        <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
                          <CalendarClock size={14} />
                          {weekProgress.completeDays}/{weekProgress.totalDays} days set
                        </div>
                      </div>

                      <AvailabilityCalendarEditor
                        value={calendar}
                        onChange={setCalendar}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                    <BadgeCheck className="mx-auto text-slate-300" size={34} />
                    <p className="mt-3 text-sm font-semibold text-slate-950">
                      Select a doctor
                    </p>
                  </div>
                )}
              </section>
            </div>
          </div>
        </main>
      </div>

      {emailOpen && selectedDoctor && (
        <div className="fixed inset-0 z-[90] flex items-end bg-slate-950/40 px-3 py-4 sm:items-center sm:justify-center sm:p-6">
          <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-950">
                  Email {selectedDoctor.name || "doctor"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  This will be sent to {selectedDoctor.email}.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setEmailOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-2 block text-xs font-medium text-slate-500">
                  Subject
                </label>
                <InputBase
                  value={emailDraft.subject}
                  onChange={(event) =>
                    setEmailDraft((prev) => ({
                      ...prev,
                      subject: event.target.value,
                    }))
                  }
                  fullWidth
                  sx={inputSx}
                  className="rounded-xl border border-slate-200 bg-white text-slate-800"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-medium text-slate-500">
                  Message
                </label>
                <InputBase
                  multiline
                  rows={6}
                  value={emailDraft.message}
                  onChange={(event) =>
                    setEmailDraft((prev) => ({
                      ...prev,
                      message: event.target.value,
                    }))
                  }
                  fullWidth
                  sx={inputSx}
                  className="rounded-xl border border-slate-200 bg-white text-slate-800"
                />
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Button
                onClick={() => setEmailOpen(false)}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>

              <Button
                onClick={sendEmail}
                disabled={
                  sendingEmail ||
                  !emailDraft.subject.trim() ||
                  !emailDraft.message.trim()
                }
                startIcon={<Send size={15} />}
                sx={primaryButtonSx}
              >
                {sendingEmail ? "Sending..." : "Send email"}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const StatCard = ({ label, value, compact = false }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white ${
      compact ? "p-3" : "p-4"
    }`}
  >
    <p className="text-sm text-slate-500">{label}</p>
    <p className="mt-2 text-2xl font-semibold text-slate-950">
      {Number(value || 0).toLocaleString()}
    </p>
  </div>
);

const Badge = ({ text, active = false }) => (
  <span
    className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
      active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
    }`}
  >
    {text}
  </span>
);

const inputSx = {
  px: 2,
  py: 1.2,
  fontSize: "14px",
  color: "#0f172a",
};

const secondaryButtonSx = {
  minHeight: 42,
  borderRadius: "12px",
  bgcolor: "#fff",
  color: "#334155",
  border: "1px solid #e2e8f0",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#f8fafc",
    boxShadow: "none",
  },
};

const primaryButtonSx = {
  minHeight: 42,
  borderRadius: "12px",
  bgcolor: "#075E54",
  color: "white",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#064b43",
    boxShadow: "none",
  },
  "&:disabled": {
    bgcolor: "#e2e8f0",
    color: "#64748b",
  },
};

export default AdminDoctorsPage;
